import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Badge,
  useToast,
  Container,
  Card,
  CardBody,
  useColorModeValue,
  Tooltip,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import {
  MdMenuBook,
  MdFullscreen,
  MdFullscreenExit,
  MdRefresh,
  MdAndroid,
  MdComputer,
  MdLock,
  MdShield,
  MdVerified,
  MdNavigateBefore,
  MdNavigateNext,
  MdZoomIn,
  MdZoomOut,
  MdFitScreen,
} from 'react-icons/md';
import { FiBookOpen, FiDownloadCloud, FiSmartphone } from 'react-icons/fi';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import CobraSpinner from '../shared/CobraSpinner';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import Sidebar from '../shared/Sidebar';
import Sidebar2 from '../shared/Sidebar2';
import Sidebar3 from '../shared/Sidebar3';

const MaylaoneBookContent = () => {
  const { user } = useContext(AuthContext);
  const { isOpen } = useSidebar();
  const toast = useToast();

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.3);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [appInfo, setAppInfo] = useState({
    android: '/uploads/apps/Maylaone-app.apk',
    windows: '/uploads/apps/Maylaone-Setup.exe',
  });

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const viewerContainerRef = useRef(null);
  const isComponentMounted = useRef(true);

  const primaryColor = '#F2C919';
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const contentMarginRight = isOpen ? { base: '0px', md: '240px' } : { base: '0px', md: '70px' };

  // Helper to ensure PDF.js library is loaded
  const ensurePdfJsLoaded = async () => {
    const origin = window.location.origin;
    const PDFJS_VERSION = '3.11.174';
    const workerPath = `${origin}/assets/pdfjs/pdf.worker.min.js?v=${PDFJS_VERSION}`;

    if (window.pdfjsLib && window.pdfjsLib.version === PDFJS_VERSION) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerPath;
      return window.pdfjsLib;
    }

    return new Promise((resolve, reject) => {
      const scriptUrl = `${origin}/assets/pdfjs/pdf.min.js?v=${PDFJS_VERSION}`;
      const existingScript = document.querySelector(`script[src*="pdf.min.js"]`);
      if (existingScript && window.pdfjsLib && window.pdfjsLib.version === PDFJS_VERSION) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerPath;
        return resolve(window.pdfjsLib);
      }

      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerPath;
          resolve(window.pdfjsLib);
        } else {
          reject(new Error('فشل تهيئة قارئ PDF'));
        }
      };
      script.onerror = () => reject(new Error('تعذر تحميل ملفات القارئ'));
      document.body.appendChild(script);
    });
  };

  // Load PDF into memory as ArrayBuffer (no download link / no blob URL exposed)
  const loadPdfDocument = async () => {
    try {
      setLoading(true);
      setError(null);

      const [pdfjs, response] = await Promise.all([
        ensurePdfJsLoaded(),
        axiosInstance.get('/maylawan/book', {
          responseType: 'arraybuffer',
        }),
      ]);

      const origin = window.location.origin;
      const loadingTask = pdfjs.getDocument({
        data: response.data,
        cMapUrl: `${origin}/assets/pdfjs/cmaps/`,
        cMapPacked: true,
        standardFontDataUrl: `${origin}/assets/pdfjs/standard_fonts/`,
        disableFontFace: false,
        disableStream: false,
        disableAutoFetch: false,
      });

      const doc = await loadingTask.promise;
      if (!isComponentMounted.current) return;

      setPdfDoc(doc);
      setTotalPages(doc.numPages);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('Error loading PDF document:', err);
      if (isComponentMounted.current) {
        setError(
          err.response?.status === 401
            ? 'يرجى تسجيل الدخول للوصول إلى كتاب مايلوان.'
            : 'تعذر فتح كتاب مايلوان. يرجى المحاولة لاحقاً.'
        );
        setLoading(false);
      }
    }
  };

  // Render the current page onto the HTML5 Canvas
  const renderPage = useCallback(
    async (pageNum) => {
      if (!pdfDoc || !canvasRef.current) return;

      try {
        setRendering(true);

        // Cancel previous render task if still in progress
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch (e) {}
        }

        const page = await pdfDoc.getPage(pageNum);
        if (!isComponentMounted.current || !canvasRef.current) return;

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // High DPI support for sharp reading
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width) + 'px';
        canvas.style.height = Math.floor(viewport.height) + 'px';

        const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

        const renderContext = {
          canvasContext: context,
          transform: transform,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;

        // Draw protective dynamic watermark across canvas
        if (context) {
          context.save();
          if (outputScale !== 1) {
            context.scale(outputScale, outputScale);
          }
          context.font = 'bold 16px Tajawal, sans-serif';
          context.fillStyle = 'rgba(15, 23, 42, 0.07)';
          context.textAlign = 'center';
          context.rotate((-30 * Math.PI) / 180);

          const studentLabel = `منصة كوبرا التعليمية • ${user?.username || user?.email || 'طالب كوبرا'} • للقراءة فقط`;
          for (let y = -viewport.height; y < viewport.height * 2; y += 180) {
            for (let x = -viewport.width; x < viewport.width * 2; x += 350) {
              context.fillText(studentLabel, x, y);
            }
          }
          context.restore();
        }

        if (isComponentMounted.current) {
          setRendering(false);
        }
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering page:', err);
        }
        if (isComponentMounted.current) {
          setRendering(false);
        }
      }
    },
    [pdfDoc, scale, user]
  );

  useEffect(() => {
    isComponentMounted.current = true;
    loadPdfDocument();

    axiosInstance
      .get('/maylawan/book-info')
      .then((res) => {
        if (res.data?.apps && isComponentMounted.current) {
          setAppInfo(res.data.apps);
        }
      })
      .catch(() => {});

    return () => {
      isComponentMounted.current = false;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale, renderPage]);

  // Anti-Save / Anti-Print Keyboard Shortcut blocker
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block Ctrl+S, Cmd+S, Ctrl+P, Cmd+P, Ctrl+U
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'u'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
        toast({
          title: 'تنبيه أمان',
          description: 'تحميل الكتاب أو طباعته غير متاح من المتصفح. يمكنك استخدام تطبيق الهاتف الرسمي.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toast]);

  // Page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageChange = (val) => {
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      setCurrentPage(num);
    }
  };

  // Zoom handlers
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.2, 2.5));
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.2, 0.7));
  const handleResetZoom = () => setScale(1.3);

  // Fullscreen
  const toggleFullscreen = () => {
    const elem = viewerContainerRef.current;
    if (!elem) return;

    if (!document.fullscreenElement) {
      elem.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const renderSidebar = () => {
    if (user?.role === 'admin') return <Sidebar2 />;
    if (user?.role === 'instructor') return <Sidebar3 />;
    return <Sidebar />;
  };

  return (
    <Box
      dir="rtl"
      fontFamily="Tajawal, sans-serif"
      minH="100vh"
      bg={useColorModeValue('#F8FAFC', '#0B1120')}
      sx={{
        '@media print': {
          '.protected-reader-container': {
            display: 'none !important',
          },
        },
      }}
    >
      <Flex minH="100vh">
        {renderSidebar()}

        <Box
          flex="1"
          mr={contentMarginRight}
          p={{ base: 3, md: 6 }}
          transition="margin-right 0.3s ease"
          maxW="100%"
        >
          <Container maxW="7xl" px={{ base: 1, md: 4 }}>
            <VStack spacing={6} align="stretch">

              {/* 1. Header Card */}
              <Card
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                borderRadius="2xl"
                shadow="sm"
                overflow="hidden"
              >
                <Box h="6px" bgGradient="linear(to-r, #F2C919, #D97706, #4F46E5)" />
                <CardBody p={{ base: 4, md: 6 }}>
                  <Flex
                    direction={{ base: 'column', md: 'row' }}
                    align={{ base: 'start', md: 'center' }}
                    justify="space-between"
                    gap={4}
                  >
                    <HStack spacing={4} align="center">
                      <Box
                        p={3}
                        borderRadius="xl"
                        bg="yellow.50"
                        color="yellow.600"
                        border="1px solid"
                        borderColor="yellow.200"
                      >
                        <Icon as={MdMenuBook} boxSize={8} />
                      </Box>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Heading size="md" color={textColor} fontWeight="bold">
                            كتاب مايلوان التعليمي (Maylaone)
                          </Heading>
                          <Badge colorScheme="purple" variant="subtle" px={2} py={0.5} borderRadius="full">
                            <HStack spacing={1}>
                              <Icon as={MdLock} boxSize={3} />
                              <Text fontSize="xs">عرض محمي للقراءة فقط</Text>
                            </HStack>
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                          يمكنك قراءة وتصفح الكتاب مباشرة هنا داخل المتصفح. لتحميل الكتاب بدون إنترنت، حمل تطبيق الهاتف.
                        </Text>
                      </Box>
                    </HStack>

                    {/* Mobile App Download Button */}
                    {appInfo.android && (
                      <Button
                        as="a"
                        href={appInfo.android}
                        download="Maylaone-app.apk"
                        leftIcon={<MdAndroid size={20} />}
                        colorScheme="yellow"
                        bg={primaryColor}
                        color="#0F172A"
                        _hover={{ bg: '#d4b016', transform: 'translateY(-1px)' }}
                        size="md"
                        borderRadius="xl"
                        fontWeight="bold"
                        px={5}
                        shadow="sm"
                      >
                        تحميل تطبيق الهاتف (Android APK)
                      </Button>
                    )}
                  </Flex>
                </CardBody>
              </Card>

              {/* 2. Secure Canvas-Based Protected Reader */}
              <Card
                ref={viewerContainerRef}
                className="protected-reader-container"
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                borderRadius="2xl"
                shadow="md"
                overflow="hidden"
                position="relative"
                onContextMenu={(e) => e.preventDefault()}
                userSelect="none"
              >
                {/* Reader Controls Toolbar */}
                <Flex
                  px={4}
                  py={3}
                  bg={useColorModeValue('gray.50', 'gray.900')}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  align="center"
                  justify="space-between"
                  wrap="wrap"
                  gap={3}
                >
                  {/* Page Navigation */}
                  <HStack spacing={2} align="center">
                    <Tooltip label="الصفحة السابقة">
                      <IconButton
                        icon={<MdNavigateNext size={22} />}
                        size="sm"
                        variant="solid"
                        colorScheme="gray"
                        aria-label="السابق"
                        onClick={goToPrevPage}
                        isDisabled={currentPage <= 1 || loading}
                      />
                    </Tooltip>

                    <HStack spacing={1.5} align="center">
                      <Text fontSize="xs" color="gray.500">
                        صفحة
                      </Text>
                      <NumberInput
                        size="xs"
                        maxW="65px"
                        min={1}
                        max={totalPages || 1}
                        value={currentPage}
                        onChange={handlePageChange}
                        isDisabled={loading}
                      >
                        <NumberInputField textAlign="center" fontWeight="bold" borderRadius="md" />
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500">
                        من {totalPages || '...'}
                      </Text>
                    </HStack>

                    <Tooltip label="الصفحة التالية">
                      <IconButton
                        icon={<MdNavigateBefore size={22} />}
                        size="sm"
                        variant="solid"
                        colorScheme="gray"
                        aria-label="التالي"
                        onClick={goToNextPage}
                        isDisabled={currentPage >= totalPages || loading}
                      />
                    </Tooltip>
                  </HStack>

                  {/* Zoom & Fullscreen Controls */}
                  <HStack spacing={1}>
                    <Tooltip label="تصغير">
                      <IconButton
                        icon={<MdZoomOut size={18} />}
                        size="sm"
                        variant="ghost"
                        aria-label="تصغير"
                        onClick={handleZoomOut}
                        isDisabled={loading}
                      />
                    </Tooltip>
                    <Text fontSize="xs" color="gray.500" minW="40px" textAlign="center">
                      {Math.round(scale * 100)}%
                    </Text>
                    <Tooltip label="تكبير">
                      <IconButton
                        icon={<MdZoomIn size={18} />}
                        size="sm"
                        variant="ghost"
                        aria-label="تكبير"
                        onClick={handleZoomIn}
                        isDisabled={loading}
                      />
                    </Tooltip>
                    <Tooltip label="الحجم الافتراضي">
                      <IconButton
                        icon={<MdFitScreen size={18} />}
                        size="sm"
                        variant="ghost"
                        aria-label="إعادة ضبط الحجم"
                        onClick={handleResetZoom}
                        isDisabled={loading}
                      />
                    </Tooltip>
                    <Tooltip label="تحديث المستند">
                      <IconButton
                        icon={<MdRefresh size={18} />}
                        size="sm"
                        variant="ghost"
                        aria-label="تحديث"
                        onClick={loadPdfDocument}
                        isLoading={loading}
                      />
                    </Tooltip>
                    <Tooltip label={isFullscreen ? 'الخروج من ملء الشاشة' : 'ملء الشاشة'}>
                      <IconButton
                        icon={isFullscreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
                        size="sm"
                        variant="ghost"
                        aria-label="ملء الشاشة"
                        onClick={toggleFullscreen}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>

                {/* Canvas Reading Canvas Display */}
                <Box
                  position="relative"
                  minH={isFullscreen ? 'calc(100vh - 60px)' : { base: '550px', md: '780px' }}
                  h={isFullscreen ? 'calc(100vh - 60px)' : { base: '550px', md: '780px' }}
                  bg="#2F3336"
                  overflow="auto"
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="center"
                  p={{ base: 2, md: 4 }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {loading && (
                    <Flex
                      position="absolute"
                      inset={0}
                      bg="rgba(15, 23, 42, 0.85)"
                      zIndex={15}
                      direction="column"
                      align="center"
                      justify="center"
                    >
                      <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
                      <Text mt={4} color="white" fontWeight="bold" fontSize="md">
                        جارٍ تجهيز كتاب مايلوان بأعلى دقة...
                      </Text>
                      <Text mt={1} color="gray.300" fontSize="xs">
                        عرض محمي وآمن مخصص لحسابك
                      </Text>
                    </Flex>
                  )}

                  {error && (
                    <Flex
                      position="absolute"
                      inset={0}
                      direction="column"
                      align="center"
                      justify="center"
                      p={6}
                      bg={cardBg}
                    >
                      <Alert
                        status="error"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        maxW="500px"
                        borderRadius="2xl"
                        p={6}
                      >
                        <AlertIcon boxSize="40px" mr={0} mb={3} />
                        <AlertTitle fontSize="lg" mb={2}>
                          تعذر تحميل الكتاب
                        </AlertTitle>
                        <AlertDescription fontSize="sm" color="gray.600" mb={4}>
                          {error}
                        </AlertDescription>
                        <Button
                          leftIcon={<MdRefresh />}
                          colorScheme="yellow"
                          bg={primaryColor}
                          color="#0F172A"
                          onClick={loadPdfDocument}
                          borderRadius="xl"
                        >
                          إعادة المحاولة
                        </Button>
                      </Alert>
                    </Flex>
                  )}

                  {/* Rendered Canvas element */}
                  <Box
                    position="relative"
                    boxShadow="0 10px 25px rgba(0,0,0,0.4)"
                    borderRadius="md"
                    overflow="hidden"
                    bg="white"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <canvas
                      ref={canvasRef}
                      style={{
                        display: 'block',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                      }}
                      onContextMenu={(e) => e.preventDefault()}
                    />

                    {/* Rendering Indicator Overlay */}
                    {rendering && (
                      <Flex
                        position="absolute"
                        top={2}
                        left={2}
                        bg="rgba(0,0,0,0.6)"
                        color="white"
                        px={2.5}
                        py={1}
                        borderRadius="full"
                        align="center"
                        gap={1.5}
                      >
                        <CobraSpinner size="xs" color="yellow.400" />
                        <Text fontSize="2xs">جارٍ التصيير...</Text>
                      </Flex>
                    )}
                  </Box>
                </Box>
              </Card>

              {/* 3. Prominent Mobile App Download Banner */}
              <Card
                bgGradient="linear(to-r, #0F172A, #1E293B)"
                color="white"
                borderRadius="2xl"
                p={5}
                shadow="md"
                border="1px solid"
                borderColor="yellow.500"
              >
                <CardBody p={2}>
                  <Flex
                    direction={{ base: 'column', md: 'row' }}
                    align={{ base: 'start', md: 'center' }}
                    justify="space-between"
                    gap={5}
                  >
                    <HStack spacing={4} align="flex-start">
                      <Box
                        p={3.5}
                        borderRadius="2xl"
                        bg="yellow.400"
                        color="#0F172A"
                        boxShadow="0 4px 15px rgba(242, 201, 25, 0.4)"
                      >
                        <Icon as={FiSmartphone} boxSize={7} />
                      </Box>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Text fontWeight="bold" fontSize="md" color="white">
                            حمّل تطبيق مايلوان للهاتف المحمول (Maylaone App)
                          </Text>
                          <Badge colorScheme="green" px={2} py={0.5} borderRadius="full">
                            بدون إنترنت
                          </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.300" maxW="650px" lineHeight="1.6">
                          احصل على تجربة قراءة سلسة ومثالية في أي وقت وأي مكان دون الحاجة لاتصال بالإنترنت، مع نظام حماية فائق ضد تصوير الشاشة وتسجيل الفيديو.
                        </Text>
                      </Box>
                    </HStack>

                    <HStack spacing={3} w={{ base: '100%', md: 'auto' }} justify={{ base: 'stretch', md: 'flex-end' }}>
                      {appInfo.android && (
                        <Button
                          as="a"
                          href={appInfo.android}
                          download="Maylaone-app.apk"
                          leftIcon={<MdAndroid size={22} />}
                          size="md"
                          colorScheme="yellow"
                          bg={primaryColor}
                          color="#0F172A"
                          _hover={{ bg: '#d4b016', transform: 'translateY(-2px)' }}
                          borderRadius="xl"
                          px={6}
                          fontWeight="bold"
                          flex={{ base: 1, md: 'none' }}
                        >
                          تحميل للأندرويد (APK)
                        </Button>
                      )}
                      {appInfo.windows && (
                        <Button
                          as="a"
                          href={appInfo.windows}
                          download="Maylaone-Setup.exe"
                          leftIcon={<MdComputer size={20} />}
                          size="md"
                          variant="outline"
                          borderColor="yellow.400"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.100' }}
                          borderRadius="xl"
                          px={5}
                          flex={{ base: 1, md: 'none' }}
                        >
                          نسخة الكمبيوتر (PC)
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>

            </VStack>
          </Container>
        </Box>
      </Flex>
    </Box>
  );
};

const MaylaoneBookPage = () => {
  return (
    <SidebarProvider>
      <MaylaoneBookContent />
    </SidebarProvider>
  );
};

export default MaylaoneBookPage;
